import type React from "react"
import type { ComponentProps } from "react"
import { classNames } from "../../utils"
import { useTable } from "./useTable"

export type TableHeaderProps = {
  className?: string
  children: React.ReactNode
} & ComponentProps<"div">

function TableHeader({ className, children, ...rest }: TableHeaderProps) {
  const { dividers } = useTable()
  return (
    <div
      className={classNames(
        dividers ? "h-10 min-h-10" : "h-6 min-h-6 pb-2",
        "gap-4 bg-bg-app",
        // Ensure container spans the entire width of its contents to ensure sticky
        // columns do not lose stickiness on sm viewports
        "inline-flex min-w-full",
        "overflow-auto",
        // Hide scrollbar
        "[-ms-overflow-style:'none'] [scrollbar-width:'none'] [&::-webkit-scrollbar]:hidden",
        className,
      )}
      role="row"
      {...rest}
    >
      {children}
    </div>
  )
}

type TableHeaderWrapperProps = {
  children: React.ReactNode
  showBorder?: boolean
} & ComponentProps<"div">

export function TableHeaderWrapper({
  children,
  showBorder,
  ...rest
}: TableHeaderWrapperProps) {
  return (
    <div
      className={classNames(
        "contents w-full flex-none",
        showBorder && "*:border-border-1 *:border-b",
      )}
      role="rowgroup"
      {...rest}
    >
      {children}
    </div>
  )
}

/**
 * The header of the  Table. If you want to make it sticky, simply
 * add `sticky top-{number} z-[1]` to the `className` prop. It should be done using
 * className so we can modify it responsively.
 */
export { TableHeader }
